import React from 'react'
import { Link } from 'react-router-dom'
import { Popover } from '@headlessui/react'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import classnames from 'classnames'

const LINK_CLASS =
  'font-medium text-slate-800 underline transition-colors hover:text-indigo-700 dark:text-slate-100 dark:hover:text-indigo-300'

export interface HeaderProgram {
  id: string
  name?: string | null
}

export interface HeaderProgramsProps {
  programs: HeaderProgram[]
}

const programLabel = (program: HeaderProgram): string => program.name || program.id

const ProgramLink: React.FC<{ program: HeaderProgram; className?: string }> = ({
  program,
  className,
}) => (
  <Link
    to={`/program/programs/${program.id}`}
    target="_blank"
    rel="noopener noreferrer"
    className={classnames('inline-flex items-center gap-0.5', LINK_CLASS, className)}
    title={programLabel(program)}
  >
    <span className="truncate">{programLabel(program)}</span>
    <ArrowTopRightOnSquareIcon className="h-3 w-3 shrink-0" />
  </Link>
)

/**
 * Sticky-header program list: show the first program inline; tuck the rest behind +N.
 * Avoids four long program names wrapping and growing the sticky header.
 */
const HeaderPrograms: React.FC<HeaderProgramsProps> = ({ programs }) => {
  if (programs.length === 0) return null

  const [first, ...rest] = programs
  const overflowCount = rest.length

  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1">
      <span className="shrink-0 text-slate-500 dark:text-slate-400">
        {programs.length === 1 ? 'Program:' : 'Programs:'}
      </span>
      <ProgramLink program={first} className="max-w-[14rem] truncate" />
      {overflowCount > 0 ? (
        <Popover className="relative shrink-0">
          <Popover.Button
            className={classnames(
              'rounded px-1 py-0.5 text-xs font-medium text-slate-600 outline-none',
              'hover:bg-slate-200 hover:text-slate-900',
              'focus-visible:ring-2 focus-visible:ring-indigo-500',
              'dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-100'
            )}
          >
            +{overflowCount} more
          </Popover.Button>
          <Popover.Panel
            anchor={{ to: 'bottom start', gap: 4 }}
            portal
            modal={false}
            transition
            className="z-[200] w-72 rounded-md border border-slate-200 bg-white p-2 shadow-lg transition duration-100 ease-out data-[closed]:translate-y-0.5 data-[closed]:opacity-0 dark:border-slate-600 dark:bg-slate-800"
          >
            <ul className="max-h-64 space-y-1 overflow-y-auto">
              {programs.map((program) => (
                <li key={program.id}>
                  <ProgramLink
                    program={program}
                    className="block w-full rounded px-2 py-1.5 text-left text-xs hover:bg-slate-100 dark:hover:bg-slate-700"
                  />
                </li>
              ))}
            </ul>
          </Popover.Panel>
        </Popover>
      ) : null}
    </span>
  )
}

export default HeaderPrograms
